import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, IconButton, CircularProgress, Alert, Divider } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useNavigate } from 'react-router-dom';

import {
  useGetAvailableAgencyShiftsQuery,
  useGetMyAgencyShiftsQuery,
} from '../slices/agencyShiftApiSlice';

// Same status logic as the chip on MyShiftsScreen — kept in sync deliberately
// so these counts always match what you'd get filtering "My Shifts" by eye.
const shiftStatus = (shift) => {
  if (shift.agencyCancelled) return 'cancelled';
  if (shift.actualStatus === 'Complete') return 'approved';
  if (shift.actualStatus === 'Not-Worked') return 'notApproved';
  const hasStarted = new Date(shift.startDateTime) <= new Date();
  return hasStarted ? 'inProgress' : 'upcoming';
};

const StatCard = ({ label, value, color, icon: Icon, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2.5,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: color,
      },
      '&:hover': onClick
        ? {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          }
        : undefined,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      </Box>
      {Icon && <Icon sx={{ color, fontSize: 30, opacity: 0.85 }} />}
    </Box>
  </Paper>
);

// Home screen: shifts available in this agency's window up top, then a
// status breakdown of the shifts this agency currently holds ("My Shifts")
// below it — the same two data sources as the Available/Mine screens, just
// summarised as counts instead of full tables.
const HomeScreen = () => {
  const navigate = useNavigate();

  const {
    data: availableShifts,
    isLoading: isLoadingAvailable,
    isFetching: isFetchingAvailable,
    error: availableError,
    refetch: refetchAvailable,
  } = useGetAvailableAgencyShiftsQuery(undefined, { pollingInterval: 30000 });

  const {
    data: myShifts,
    isLoading: isLoadingMine,
    isFetching: isFetchingMine,
    error: mineError,
    refetch: refetchMine,
  } = useGetMyAgencyShiftsQuery();

  const statusCounts = useMemo(() => {
    const counts = { upcoming: 0, inProgress: 0, approved: 0, notApproved: 0, cancelled: 0 };
    (myShifts || []).forEach((shift) => {
      counts[shiftStatus(shift)] += 1;
    });
    return counts;
  }, [myShifts]);

  const isLoading = isLoadingAvailable || isLoadingMine;
  const isFetching = isFetchingAvailable || isFetchingMine;

  const statusCards = [
    { key: 'upcoming', label: 'Upcoming', color: '#2196f3', icon: UpcomingIcon },
    { key: 'inProgress', label: 'In Progress / Past', color: '#757575', icon: HourglassBottomIcon },
    { key: 'approved', label: 'Approved', color: '#2e7d32', icon: CheckCircleOutlineIcon },
    { key: 'notApproved', label: 'Not Approved', color: '#ed6c02', icon: ErrorOutlineIcon },
    { key: 'cancelled', label: 'Cancelled', color: '#c62828', icon: CancelOutlinedIcon },
  ];

  return (
    <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: { xs: 2, sm: 3 }, minHeight: '80vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Home
          </Typography>
          <IconButton
            onClick={() => {
              refetchAvailable();
              refetchMine();
            }}
            disabled={isFetching}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {(availableError || mineError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {availableError?.data?.message || mineError?.data?.message || 'Failed to load shift counts'}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper
            elevation={2}
            sx={{ p: { xs: 3, sm: 5 }, mt: 5, borderRadius: 4, bgcolor: 'background.paper' }}
          >
            <Typography
              align="center"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 2,
              }}
            >
              This Agency's View
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Shifts Available"
                  value={availableShifts?.length || 0}
                  color="#0b5394"
                  icon={EventAvailableIcon}
                  onClick={() => navigate('/shifts/available')}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography
              align="center"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 2,
              }}
            >
              My Shifts by Status
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {statusCards.map(({ key, label, color, icon }) => (
                <Grid item xs={6} sm={4} md={2.4} key={key}>
                  <StatCard
                    label={label}
                    value={statusCounts[key]}
                    color={color}
                    icon={icon}
                    onClick={() => navigate('/shifts/mine')}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default HomeScreen;
